import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FaTachometerAlt,
  FaGlobe,
  FaCity,
  FaUmbrellaBeach,
  FaCog,
  FaUsers,
  FaClock,
  FaChartLine,
  FaCogs,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaEye,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaUserFriends,
  FaList,
  FaFilter,
  FaDownload,
  FaUser,
  FaCaretDown,
  FaSave,
  FaTimes as FaClose,
  FaBuilding,
  FaChartPie,
} from "react-icons/fa";

const API_BASE_URL = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";

const AdminActivity = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // Filter states
  const [countryFilter, setCountryFilter] = useState(""); // For cities table
  const [cityFilter, setCityFilter] = useState(""); // For activities table
  const [activityFilter, setActivityFilter] = useState(""); // For activity options table (old, can be removed later)
  
  // Additional filters for activity options table
  const [optionCountryFilter, setOptionCountryFilter] = useState(""); // For activity options table
  const [optionCityFilter, setOptionCityFilter] = useState(""); // For activity options table
  const [optionActivityFilter, setOptionActivityFilter] = useState(""); // For activity options table

  // Stats state
  const [stats, setStats] = useState({
    totalCountries: 0,
    totalCities: 0,
    totalActivities: 0,
    totalBookings: 0,
    totalRevenue: 0,
    totalUsers: 0,
  });

  // Data states
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [timePricing, setTimePricing] = useState([]);
  const [bookings, setBookings] = useState([]);

  // Form states
  const [newCountry, setNewCountry] = useState({
    name: "",
    code: "",
    currency: "INR",
    currencySymbol: "₹",
    active: true,
  });
  const [newCity, setNewCity] = useState({
    countryId: "",
    name: "",
    timezone: "Asia/Kolkata",
    active: true,
  });
  const [newActivity, setNewActivity] = useState({
    cityId: "",
    name: "",
    description: "",
    duration: 60,
    maxCapacity: 10,
    status: "active",
  });
  const [newOption, setNewOption] = useState({
    activityId: "",
    name: "",
    basePrice: 0,
    duration: 60,
    maxCapacity: 10,
    active: true,
  });
  const [newVisitorType, setNewVisitorType] = useState({
    name: "",
    minAge: 0,
    maxAge: 100,
    priceMultiplier: 1.0,
    active: true,
  });

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // State for cascading dropdowns in activity option form
  const [optionSelectedCountry, setOptionSelectedCountry] = useState("");
  const [optionSelectedCity, setOptionSelectedCity] = useState("");
  const [newTimePricing, setNewTimePricing] = useState({
    activityId: "",
    dayType: "weekday",
    startTime: "09:00",
    endTime: "17:00",
    multiplier: 1.0,
    label: "",
    active: true,
  });

  // Image upload states
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, [activeSection]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Base endpoints that should exist
      const endpoints = [
        { key: "countries", url: `${API_BASE_URL}/api/activity/admin/countries` },
        { key: "cities", url: `${API_BASE_URL}/api/activity/admin/cities` },
        { key: "activities", url: `${API_BASE_URL}/api/activity/admin/activities` },
      ];

      const results = {};

      // Fetch data with error handling for each endpoint
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint.url);
          results[endpoint.key] = response.data?.data || [];
        } catch (error) {
          console.warn(`Failed to fetch ${endpoint.key}:`, error.message);
          results[endpoint.key] = [];
        }
      }

      // Set states with fallback empty arrays
      setCountries(results.countries);
      setCities(results.cities);
      setActivities(results.activities);

      // Update stats
      setStats({
        totalCountries: results.countries.length || 0,
        totalCities: results.cities.length || 0,
        totalActivities: results.activities.length || 0,
        totalBookings: 0, // You need to implement booking endpoint
        totalRevenue: 0,
        totalUsers: 0,
      });

      // Fetch additional data based on active section
      if (activeSection === "options") {
        try {
          const optionsRes = await axios.get(
            `${API_BASE_URL}/api/activity/admin/activity-options`
          );
          setActivityOptions(optionsRes.data?.data || []);
        } catch (error) {
          console.warn("Failed to fetch options:", error.message);
          setActivityOptions([]);
        }
      }

      if (activeSection === "visitor-types") {
        try {
          const visitorTypesRes = await axios.get(
            `${API_BASE_URL}/api/activity/admin/visitor-types`
          );
          setVisitorTypes(visitorTypesRes.data?.data || []);
        } catch (error) {
          console.warn("Failed to fetch visitor types:", error.message);
          setVisitorTypes([]);
        }
      }

      if (activeSection === "time-pricing") {
        try {
          const timePricingRes = await axios.get(
            `${API_BASE_URL}/api/activity/admin/time-pricing`
          );
          setTimePricing(timePricingRes.data?.data || []);
        } catch (error) {
          console.warn("Failed to fetch time pricing:", error.message);
          setTimePricing([]);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      alert(`Error fetching data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // CRUD Operations
  // handleCreate function mein time-pricing case ko update karo
  const handleCreate = async (type, data, imageFile = null, imageFiles = null) => {
    try {
      let endpoint = "";
      let successMessage = "";

      // Create FormData for image uploads
      const formData = new FormData();

      switch (type) {
        case "country":
          endpoint = `${API_BASE_URL}/api/activity/admin/countries`;
          successMessage = "Country created successfully!";
          formData.append("name", data.name);
          formData.append("code", data.code);
          formData.append("currency", data.currency);
          formData.append("currencySymbol", data.currencySymbol);
          formData.append("active", data.active !== undefined ? data.active : true);
          if (imageFile) {
            formData.append("image", imageFile);
          }
          break;
        case "city":
          endpoint = `${API_BASE_URL}/api/activity/admin/cities`;
          successMessage = "City created successfully!";
          formData.append("countryId", data.countryId);
          formData.append("name", data.name);
          formData.append("timezone", data.timezone);
          formData.append("active", data.active !== undefined ? data.active : true);
          if (imageFile) {
            formData.append("image", imageFile);
          }
          break;
        case "activity":
          endpoint = `${API_BASE_URL}/api/activity/admin/activities`;
          successMessage = "Activity created successfully!";
          formData.append("cityId", data.cityId);
          formData.append("name", data.name);
          formData.append("description", data.description);
          formData.append("duration", data.duration);
          formData.append("maxCapacity", data.maxCapacity);
          formData.append("status", data.status || "active");
          if (imageFiles && imageFiles.length > 0) {
            imageFiles.forEach((file) => {
              formData.append("images", file);
            });
          }
          break;
        case "option":
          endpoint = `${API_BASE_URL}/api/activity/admin/activity-options`;
          successMessage = "Activity option created successfully!";
          // Options don't have images, use regular JSON
          await axios.post(endpoint, data);
          alert(`✅ ${successMessage}`);
          fetchDashboardData();
          setShowAddModal(false);
          setEditingItem(null);
          resetFormAndImages(type);
          return;
        case "visitor-type":
          endpoint = `${API_BASE_URL}/api/activity/admin/visitor-types`;
          successMessage = "Visitor type created successfully!";
          // Visitor types don't have images, use regular JSON
          await axios.post(endpoint, data);
          alert(`✅ ${successMessage}`);
          fetchDashboardData();
          setShowAddModal(false);
          setEditingItem(null);
          resetFormAndImages(type);
          return;
        case "time-pricing":
          endpoint = `${API_BASE_URL}/api/activity/admin/time-pricing`;
          successMessage = "Time pricing created successfully!";
          // Time pricing doesn't have images, use regular JSON
          const timePricingData = {
            activityId: data.activityId,
            dayType: data.dayType,
            startTime: data.startTime,
            endTime: data.endTime,
            multiplier: parseFloat(data.multiplier),
            label: data.label,
            active: data.active !== undefined ? data.active : true,
          };
          await axios.post(endpoint, timePricingData);
          alert(`✅ ${successMessage}`);
          fetchDashboardData();
          setShowAddModal(false);
          setEditingItem(null);
          resetFormAndImages(type);
          return;
        default:
          return;
      }

      // Send FormData for types with images
      await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      alert(`✅ ${successMessage}`);
      fetchDashboardData();
      setShowAddModal(false);
      setEditingItem(null);
      resetFormAndImages(type);
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const resetFormAndImages = (type) => {
    setSelectedImage(null);
    setSelectedImages([]);
    setImagePreview(null);
    setImagePreviews([]);
    setExistingImages([]);

    switch (type) {
      case "country":
        setNewCountry({
          name: "",
          code: "",
          currency: "INR",
          currencySymbol: "₹",
          active: true,
        });
        break;
      case "city":
        setNewCity({
          countryId: "",
          name: "",
          timezone: "Asia/Kolkata",
          active: true,
        });
        break;
      case "activity":
        setNewActivity({
          cityId: "",
          name: "",
          description: "",
          duration: 60,
          maxCapacity: 10,
          status: "active",
        });
        break;
      case "option":
        setNewOption({
          activityId: "",
          name: "",
          basePrice: 0,
          duration: 60,
          maxCapacity: 10,
          active: true,
        });
        // Reset cascading dropdowns
        setOptionSelectedCountry("");
        setOptionSelectedCity("");
        break;
      case "visitor-type":
        setNewVisitorType({
          name: "",
          minAge: 0,
          maxAge: 100,
          priceMultiplier: 1.0,
          active: true,
        });
        break;
      case "time-pricing":
        setNewTimePricing({
          activityId: "",
          dayType: "weekday",
          startTime: "09:00",
          endTime: "17:00",
          multiplier: 1.0,
          label: "",
          active: true,
        });
        break;
    }
  };

  const handleUpdate = async (type, id, data, imageFile = null, imageFiles = null) => {
    try {
      let endpoint = `${API_BASE_URL}/api/activity/admin/${type}/${id}`;

      // Create FormData for image uploads
      const formData = new FormData();

      switch (type) {
        case "countries":
        case "country":
          formData.append("name", data.name);
          formData.append("code", data.code);
          formData.append("currency", data.currency);
          formData.append("currencySymbol", data.currencySymbol);
          formData.append("active", data.active !== undefined ? data.active : true);
          if (imageFile) {
            formData.append("image", imageFile);
          } else if (data.image) {
            formData.append("image", data.image);
          }
          await axios.put(endpoint, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          break;
        case "cities":
        case "city":
          formData.append("countryId", data.countryId);
          formData.append("name", data.name);
          formData.append("timezone", data.timezone);
          formData.append("active", data.active !== undefined ? data.active : true);
          if (imageFile) {
            formData.append("image", imageFile);
          } else if (data.image) {
            formData.append("image", data.image);
          }
          await axios.put(endpoint, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          break;
        case "activities":
        case "activity":
          formData.append("cityId", data.cityId);
          formData.append("name", data.name);
          formData.append("description", data.description);
          formData.append("duration", data.duration);
          formData.append("maxCapacity", data.maxCapacity);
          formData.append("status", data.status || "active");
          
          // Handle existing images
          if (existingImages.length > 0) {
            formData.append("existingImages", JSON.stringify(existingImages));
          }
          
          // Add new images
          if (imageFiles && imageFiles.length > 0) {
            imageFiles.forEach((file) => {
              formData.append("images", file);
            });
          }
          
          await axios.put(endpoint, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          break;
        case "time-pricing":
          const timePricingData = {
            activityId: data.activityId,
            dayType: data.dayType,
            startTime: data.startTime,
            endTime: data.endTime,
            multiplier: parseFloat(data.multiplier),
            label: data.label,
            active: data.active !== undefined ? data.active : true,
          };
          await axios.put(endpoint, timePricingData);
          break;
        default:
          // For other types without images, use regular JSON
          await axios.put(endpoint, data);
      }

      alert(`✅ ${type.replace("-", " ")} updated successfully!`);
      fetchDashboardData();
      setEditingItem(null);
      setShowAddModal(false);
      resetFormAndImages(type);
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (type, id) => {
    if (
      window.confirm(
        `Are you sure you want to delete this ${type.replace("-", " ")}?`
      )
    ) {
      try {
        await axios.delete(`${API_BASE_URL}/api/activity/admin/${type}/${id}`);
        alert(`✅ ${type.replace("-", " ")} deleted successfully!`);
        fetchDashboardData();
      } catch (error) {
        alert(`❌ Error: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleToggleStatus = async (type, id, currentStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/api/activity/admin/${type}/${id}`, {
        active: !currentStatus,
      });
      fetchDashboardData();
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  // Image upload handlers
  const handleImageSelect = (e, isMultiple = false) => {
    const files = e.target.files;
    if (isMultiple) {
      const newFiles = Array.from(files);
      setSelectedImages((prev) => [...prev, ...newFiles]);
      const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
      setImagePreviews((prev) => [...prev, ...newPreviews]);
    } else {
      if (files && files[0]) {
        setSelectedImage(files[0]);
        setImagePreview(URL.createObjectURL(files[0]));
      }
    }
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    const cleanPath = imagePath.replace(/\\/g, "/");
    if (cleanPath.startsWith("http")) return cleanPath;
    return `${API_BASE_URL}/${cleanPath}`;
  };

  // Navigation items
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: <FaTachometerAlt /> },
    { id: "countries", label: "Countries", icon: <FaGlobe /> },
    { id: "cities", label: "Cities", icon: <FaCity /> },
    { id: "activities", label: "Activities", icon: <FaUmbrellaBeach /> },
    { id: "options", label: "Options", icon: <FaCog /> },
    { id: "visitor-types", label: "Visitor Types", icon: <FaUserFriends /> },
    { id: "time-pricing", label: "Time Pricing", icon: <FaClock /> },
    { id: "bookings", label: "Bookings", icon: <FaList /> },
    { id: "reports", label: "Reports", icon: <FaChartPie /> },
  ];

  // Stats cards
  const statCards = [
    {
      label: "Total Countries",
      value: stats.totalCountries,
      icon: <FaGlobe />,
      color: "bg-blue-500",
    },
    {
      label: "Total Cities",
      value: stats.totalCities,
      icon: <FaCity />,
      color: "bg-green-500",
    },
    {
      label: "Total Activities",
      value: stats.totalActivities,
      icon: <FaUmbrellaBeach />,
      color: "bg-purple-500",
    },
    {
      label: "Total Bookings",
      value: stats.totalBookings,
      icon: <FaList />,
      color: "bg-yellow-500",
    },
    {
      label: "Total Revenue",
      value: `$${stats.totalRevenue}`,
      icon: <FaMoneyBillWave />,
      color: "bg-indigo-500",
    },
    {
      label: "Total Users",
      value: stats.totalUsers,
      icon: <FaUsers />,
      color: "bg-pink-500",
    },
  ];

  // Render Dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Dashboard Overview
          </h2>
          <p className="text-gray-600">Welcome to Activity Management System</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.slice(0, 3).map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {stat.value}
                </p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <div className="text-white text-xl">{stat.icon}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              setModalType("country");
              setShowAddModal(true);
            }}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center"
          >
            <FaGlobe className="text-blue-500 text-2xl mx-auto mb-2" />
            <span className="font-medium">Add Country</span>
          </button>
          <button
            onClick={() => {
              setModalType("city");
              setShowAddModal(true);
            }}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center"
          >
            <FaCity className="text-green-500 text-2xl mx-auto mb-2" />
            <span className="font-medium">Add City</span>
          </button>
          <button
            onClick={() => {
              setModalType("activity");
              setShowAddModal(true);
            }}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center"
          >
            <FaUmbrellaBeach className="text-purple-500 text-2xl mx-auto mb-2" />
            <span className="font-medium">Add Activity</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Render Countries Management
  const renderCountries = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Countries Management
          </h2>
          <p className="text-gray-600">Manage all countries in the system</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => {
              setModalType("country");
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add Country
          </button>
        </div>
      </div>

      {/* Countries Table */}
      <div className="bg-white rounded-xl shadow-md border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Currency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {countries && countries.length > 0 ? (
                countries
                  .filter(
                    (c) =>
                      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (c.code || "")
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                  )
                  .map((country) => (
                    <tr key={country._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {country.image ? (
                            <img
                              src={getImageUrl(country.image)}
                              alt={country.name}
                              className="h-10 w-10 rounded-lg object-cover mr-3"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div className={`h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 ${country.image ? "hidden" : ""}`}>
                            <span className="text-sm font-bold text-blue-700">
                              {(country.code || "").substring(0, 2)}
                            </span>
                          </div>
                          <div className="font-medium text-gray-900">
                            {country.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                          {country.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {country.currency}
                        </div>
                        <div className="text-xs text-gray-500">
                          {country.currencySymbol}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            country.active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {country.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => {
                              setEditingItem({ ...country, type: "countries" });
                              setEditFormData(country);
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() =>
                              handleDelete("countries", country._id)
                            }
                            className="text-red-600 hover:text-red-900"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No countries found. Add your first country!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Cities Management
  const renderCities = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Cities Management
          </h2>
          <p className="text-gray-600">Manage cities by country</p>
        </div>
        <div className="flex items-center space-x-4 flex-wrap gap-2">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search cities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country._id} value={country._id}>
                {country.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setModalType("city");
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add City
          </button>
        </div>
      </div>

      {/* Cities Table */}
      <div className="bg-white rounded-xl shadow-md border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timezone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cities && cities.length > 0 ? (
                cities
                  .filter((c) => {
                    // Search filter
                    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
                    // Country filter
                    const countryId = c.countryId && typeof c.countryId === 'object' ? c.countryId._id : c.countryId;
                    const matchesCountry = !countryFilter || countryId === countryFilter || countryId?.toString() === countryFilter;
                    return matchesSearch && matchesCountry;
                  })
                  .map((city) => {
                    // Handle both populated and non-populated countryId
                    const country = city.countryId && typeof city.countryId === 'object' 
                      ? city.countryId 
                      : countries.find((co) => co._id === city.countryId || co._id?.toString() === city.countryId?.toString());
                    const countryName = country?.name || (typeof city.countryId === 'object' ? city.countryId?.name : null) || "N/A";
                    const countryCode = country?.code || (typeof city.countryId === 'object' ? city.countryId?.code : null) || "";
                    
                    return (
                      <tr key={city._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {city.image ? (
                              <img
                                src={getImageUrl(city.image)}
                                alt={city.name}
                                className="h-10 w-10 rounded-lg object-cover mr-3"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div className={`h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 ${city.image ? "hidden" : ""}`}>
                              <FaBuilding className="text-purple-600" />
                            </div>
                            <div className="font-medium text-gray-900">
                              {city.name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {countryCode && (
                              <div className="h-6 w-6 bg-blue-100 rounded flex items-center justify-center mr-2">
                                <span className="text-xs font-bold text-blue-700">
                                  {countryCode.substring(0, 2)}
                                </span>
                              </div>
                            )}
                            <span>{countryName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {city.timezone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              city.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {city.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => {
                                setEditingItem({ ...city, type: "cities" });
                                setEditFormData(city);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleDelete("cities", city._id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No cities found. Add your first city!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Activities Management
  const renderActivities = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Activities Management
          </h2>
          <p className="text-gray-600">Manage all activities in the system</p>
        </div>
        <div className="flex items-center space-x-4 flex-wrap gap-2">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Cities</option>
            {cities.map((city) => (
              <option key={city._id} value={city._id}>
                {city.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setModalType("activity");
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add Activity
          </button>
        </div>
      </div>

      {/* Activities Table */}
      <div className="bg-white rounded-xl shadow-md border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capacity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activities && activities.length > 0 ? (
                activities
                  .filter((a) => {
                    // Search filter
                    const matchesSearch = 
                      a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (a.description || "")
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase());
                    // City filter
                    const cityId = a.cityId && typeof a.cityId === 'object' ? a.cityId._id : a.cityId;
                    const matchesCity = !cityFilter || cityId === cityFilter || cityId?.toString() === cityFilter;
                    return matchesSearch && matchesCity;
                  })
                  .map((activity) => {
                    // Handle both populated and non-populated cityId
                    const city = activity.cityId && typeof activity.cityId === 'object' 
                      ? activity.cityId 
                      : cities.find((c) => c._id === activity.cityId || c._id?.toString() === activity.cityId?.toString());
                    const cityName = city?.name || (typeof activity.cityId === 'object' ? activity.cityId?.name : null) || "N/A";
                    
                    return (
                      <tr key={activity._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            {activity.images && activity.images.length > 0 ? (
                              <img
                                src={getImageUrl(activity.images[0])}
                                alt={activity.name}
                                className="h-12 w-12 rounded-lg object-cover mr-3"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div className={`h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 ${activity.images && activity.images.length > 0 ? "hidden" : ""}`}>
                              <FaUmbrellaBeach className="text-green-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {activity.name}
                              </div>
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {activity.description}
                              </div>
                              {activity.images && activity.images.length > 1 && (
                                <div className="text-xs text-blue-600 mt-1">
                                  +{activity.images.length - 1} more images
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {cityName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                            {activity.duration} min
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {activity.maxCapacity}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              activity.status === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {activity.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => {
                                setEditingItem({
                                  ...activity,
                                  type: "activities",
                                });
                                setEditFormData(activity);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete("activities", activity._id)
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No activities found. Add your first activity!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Options Management
  const renderOptions = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Activity Options</h2>
          <p className="text-gray-600">Manage pricing options for activities</p>
        </div>
        <div className="flex items-center space-x-4 flex-wrap gap-2">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={optionCountryFilter}
            onChange={(e) => {
              setOptionCountryFilter(e.target.value);
              setOptionCityFilter(""); // Reset city filter when country changes
              setOptionActivityFilter(""); // Reset activity filter when country changes
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country._id} value={country._id}>
                {country.name}
              </option>
            ))}
          </select>
          <select
            value={optionCityFilter}
            onChange={(e) => {
              setOptionCityFilter(e.target.value);
              setOptionActivityFilter(""); // Reset activity filter when city changes
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            disabled={!optionCountryFilter}
          >
            <option value="">All Cities</option>
            {cities
              .filter((city) => {
                const cityCountryId = city.countryId && typeof city.countryId === 'object' 
                  ? city.countryId._id 
                  : city.countryId;
                return !optionCountryFilter || cityCountryId === optionCountryFilter || cityCountryId?.toString() === optionCountryFilter;
              })
              .map((city) => (
                <option key={city._id} value={city._id}>
                  {city.name}
                </option>
              ))}
          </select>
          <select
            value={optionActivityFilter}
            onChange={(e) => setOptionActivityFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            disabled={!optionCityFilter}
          >
            <option value="">All Activities</option>
            {activities
              .filter((activity) => {
                const activityCityId = activity.cityId && typeof activity.cityId === 'object' 
                  ? activity.cityId._id 
                  : activity.cityId;
                return !optionCityFilter || activityCityId === optionCityFilter || activityCityId?.toString() === optionCityFilter;
              })
              .map((activity) => (
                <option key={activity._id} value={activity._id}>
                  {activity.name}
                </option>
              ))}
          </select>
          <button
            onClick={() => {
              setModalType("option");
              // Reset cascading dropdowns when opening modal for new option
              setOptionSelectedCountry("");
              setOptionSelectedCity("");
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add Option
          </button>
        </div>
      </div>

      {/* Options Table */}
      <div className="bg-white rounded-xl shadow-md border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Option
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capacity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activityOptions && activityOptions.length > 0 ? (
                activityOptions
                  .filter((o) => {
                    // Search filter
                    const matchesSearch = o.name.toLowerCase().includes(searchTerm.toLowerCase());
                    
                    // Get activity, city, and country from populated data
                    const activity = o.activityId && typeof o.activityId === 'object' 
                      ? o.activityId 
                      : activities.find((a) => a._id === o.activityId || a._id?.toString() === o.activityId?.toString());
                    
                    const city = activity?.cityId && typeof activity.cityId === 'object' 
                      ? activity.cityId 
                      : (activity ? cities.find((c) => c._id === activity.cityId || c._id?.toString() === activity.cityId?.toString()) : null);
                    
                    const country = city?.countryId && typeof city.countryId === 'object' 
                      ? city.countryId 
                      : (city ? countries.find((co) => co._id === city.countryId || co._id?.toString() === city.countryId?.toString()) : null);
                    
                    // Country filter
                    const countryId = country?._id || (country && typeof country === 'object' ? country._id : null);
                    const matchesCountry = !optionCountryFilter || countryId === optionCountryFilter || countryId?.toString() === optionCountryFilter;
                    
                    // City filter
                    const cityId = city?._id || (city && typeof city === 'object' ? city._id : null);
                    const matchesCity = !optionCityFilter || cityId === optionCityFilter || cityId?.toString() === optionCityFilter;
                    
                    // Activity filter
                    const activityId = activity?._id || (activity && typeof activity === 'object' ? activity._id : null);
                    const matchesActivity = !optionActivityFilter || activityId === optionActivityFilter || activityId?.toString() === optionActivityFilter;
                    
                    return matchesSearch && matchesCountry && matchesCity && matchesActivity;
                  })
                  .map((option) => {
                    // Handle both populated and non-populated activityId
                    const activity = option.activityId && typeof option.activityId === 'object' 
                      ? option.activityId 
                      : activities.find((a) => a._id === option.activityId || a._id?.toString() === option.activityId?.toString());
                    
                    const activityName = activity?.name || (typeof option.activityId === 'object' ? option.activityId?.name : null) || "N/A";
                    
                    // Get city from activity
                    const city = activity?.cityId && typeof activity.cityId === 'object' 
                      ? activity.cityId 
                      : (activity ? cities.find((c) => c._id === activity.cityId || c._id?.toString() === activity.cityId?.toString()) : null);
                    const cityName = city?.name || (typeof activity?.cityId === 'object' ? activity.cityId?.name : null) || "N/A";
                    
                    // Get country from city
                    const country = city?.countryId && typeof city.countryId === 'object' 
                      ? city.countryId 
                      : (city ? countries.find((co) => co._id === city.countryId || co._id?.toString() === city.countryId?.toString()) : null);
                    const countryName = country?.name || (typeof city?.countryId === 'object' ? city.countryId?.name : null) || "N/A";
                    
                    return (
                      <tr key={option._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">
                            {option.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {countryName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {cityName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {activityName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
                            ₹{option.basePrice}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {option.duration} min
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {option.maxCapacity}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              option.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {option.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => {
                                setEditingItem({
                                  ...option,
                                  type: "activity-options",
                                });
                                setEditFormData(option);
                                
                                // Extract country and city from populated activity data
                                const activity = option.activityId && typeof option.activityId === 'object' 
                                  ? option.activityId 
                                  : activities.find((a) => a._id === option.activityId || a._id?.toString() === option.activityId?.toString());
                                
                                if (activity) {
                                  const city = activity.cityId && typeof activity.cityId === 'object' 
                                    ? activity.cityId 
                                    : cities.find((c) => c._id === activity.cityId || c._id?.toString() === activity.cityId?.toString());
                                  
                                  if (city) {
                                    const cityId = city._id || (typeof city === 'string' ? city : null);
                                    if (cityId) {
                                      setOptionSelectedCity(cityId.toString());
                                    }
                                    
                                    const country = city.countryId && typeof city.countryId === 'object' 
                                      ? city.countryId 
                                      : countries.find((co) => co._id === city.countryId || co._id?.toString() === city.countryId?.toString());
                                    
                                    if (country) {
                                      const countryId = country._id || (typeof country === 'string' ? country : null);
                                      if (countryId) {
                                        setOptionSelectedCountry(countryId.toString());
                                      }
                                    }
                                  }
                                }
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete("activity-options", option._id)
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No options found. Add your first option!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  // Render Visitor Types Management
  const renderVisitorTypes = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Visitor Types Management
          </h2>
          <p className="text-gray-600">
            Manage visitor categories and pricing multipliers
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search visitor types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => {
              setModalType("visitor-type");
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add Visitor Type
          </button>
        </div>
      </div>

      {/* Visitor Types Table */}
      <div className="bg-white rounded-xl shadow-md border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visitor Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Age Range
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price Multiplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visitorTypes && visitorTypes.length > 0 ? (
                visitorTypes
                  .filter((vt) =>
                    vt.name.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((visitorType) => (
                    <tr key={visitorType._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {visitorType.name}
                        </div>
                        {visitorType.description && (
                          <div className="text-sm text-gray-500">
                            {visitorType.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                          {visitorType.minAge} - {visitorType.maxAge} years
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm font-bold">
                          {visitorType.priceMultiplier}x
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            visitorType.active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {visitorType.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => {
                              setEditingItem({
                                ...visitorType,
                                type: "visitor-types",
                              });
                              setEditFormData(visitorType);
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() =>
                              handleDelete("visitor-types", visitorType._id)
                            }
                            className="text-red-600 hover:text-red-900"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No visitor types found. Add your first visitor type!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  // Render Time Pricing Management
  const renderTimePricing = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Time-Based Pricing
          </h2>
          <p className="text-gray-600">
            Manage dynamic pricing based on time slots
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search time pricing rules..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => {
              setModalType("time-pricing");
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add Time Pricing Rule
          </button>
        </div>
      </div>

      {/* Time Pricing Table */}
      <div className="bg-white rounded-xl shadow-md border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Day Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Slot
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price Multiplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Label
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {timePricing && timePricing.length > 0 ? (
                timePricing
                  .filter((tp) =>
                    tp.label.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((pricing) => {
                    // Handle both populated and non-populated activityId
                    const activity = pricing.activityId && typeof pricing.activityId === 'object' 
                      ? pricing.activityId 
                      : activities.find((a) => a._id === pricing.activityId || a._id?.toString() === pricing.activityId?.toString());
                    const activityName = activity?.name || (typeof pricing.activityId === 'object' ? pricing.activityId?.name : null) || "N/A";
                    
                    return (
                      <tr key={pricing._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {activityName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              pricing.dayType === "weekend"
                                ? "bg-purple-100 text-purple-800"
                                : pricing.dayType === "holiday"
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {pricing.dayType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">
                            {pricing.startTime} - {pricing.endTime}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm font-bold">
                            {pricing.multiplier}x
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {pricing.label}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              pricing.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {pricing.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => {
                                setEditingItem({
                                  ...pricing,
                                  type: "time-pricing",
                                });
                                setEditFormData(pricing);
                              }}
                              className="text-green-600 hover:text-green-900"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete("time-pricing", pricing._id)
                              }
                              className="text-red-600 hover:text-red-900"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No time pricing rules found. Add your first rule!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  // Render Bookings Management (Placeholder)
  // Render Bookings Management
  const renderBookings = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Bookings Management
          </h2>
          <p className="text-gray-600">View and manage all bookings</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => {
              // Booking filter modal open karega
            }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
          >
            <FaFilter className="mr-2" />
            Filter
          </button>
          <button
            onClick={() => {
              // Export bookings
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
          >
            <FaDownload className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Bookings Table - Yahan backend se data fetch karna hoga */}
      <div className="bg-white rounded-xl shadow-md border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Ref
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Yahan bookings data map karna hoga */}
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  Bookings feature is being implemented. Please check back soon!
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Reports (Placeholder)
  const renderReports = () => (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Reports & Analytics
          </h2>
          <p className="text-gray-600">
            View system analytics and generate reports
          </p>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-md border p-8 text-center">
        <FaChartPie className="text-4xl text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Reports Feature Coming Soon
        </h3>
        <p className="text-gray-600">
          The reporting and analytics system will be available in the next
          update.
        </p>
      </div>
    </div>
  );

  // Initialize existing images when editing activity
  useEffect(() => {
    if (editingItem && editingItem.type === "activity" && editFormData.images && Array.isArray(editFormData.images)) {
      setExistingImages(editFormData.images);
    } else if (!editingItem) {
      setExistingImages([]);
    }
  }, [editingItem, editFormData.images]);

  // Render Modal
  const renderModal = () => {
    if (!showAddModal && !editingItem) return null;

    const isEdit = !!editingItem;
    const type = isEdit ? editingItem.type : modalType;

    // Get form data based on type
    let formData;
    if (isEdit) {
      formData = editFormData;
    } else {
      switch (type) {
        case "country":
          formData = newCountry;
          break;
        case "city":
          formData = newCity;
          break;
        case "activity":
          formData = newActivity;
          break;
        case "option":
          formData = newOption;
          break;
        case "visitor-type":
          formData = newVisitorType;
          break;
        case "time-pricing":
          formData = newTimePricing;
          break;
        default:
          formData = {};
      }
    }

    const handleFormChange = (field, value) => {
      if (isEdit) {
        setEditFormData((prev) => ({ ...prev, [field]: value }));
      } else {
        switch (type) {
          case "country":
            setNewCountry((prev) => ({ ...prev, [field]: value }));
            break;
          case "city":
            setNewCity((prev) => ({ ...prev, [field]: value }));
            break;
          case "activity":
            setNewActivity((prev) => ({ ...prev, [field]: value }));
            break;
          case "option":
            setNewOption((prev) => ({ ...prev, [field]: value }));
            break;
          case "visitor-type":
            setNewVisitorType((prev) => ({ ...prev, [field]: value }));
            break;
          case "time-pricing":
            setNewTimePricing((prev) => ({ ...prev, [field]: value }));
            break;
        }
      }
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      if (isEdit) {
        handleUpdate(type, editingItem._id, formData, selectedImage, selectedImages);
      } else {
        handleCreate(type, formData, selectedImage, selectedImages);
      }
    };

    const getModalTitle = () => {
      const titles = {
        country: "Country",
        city: "City",
        activity: "Activity",
        option: "Activity Option",
        "visitor-type": "Visitor Type",
        "time-pricing": "Time Pricing",
      };
      return `${isEdit ? "Edit" : "Add New"} ${titles[type] || type}`;
    };

    const renderFormFields = () => {
      switch (type) {
        case "country":
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country Code *
                </label>
                <input
                  type="text"
                  value={formData.code || ""}
                  onChange={(e) =>
                    handleFormChange("code", e.target.value.toUpperCase())
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength="3"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency *
                </label>
                <input
                  type="text"
                  value={formData.currency || ""}
                  onChange={(e) => handleFormChange("currency", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency Symbol *
                </label>
                <input
                  type="text"
                  value={formData.currencySymbol || ""}
                  onChange={(e) =>
                    handleFormChange("currencySymbol", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country Image
                </label>
                <div className="space-y-4">
                  {(imagePreview || (isEdit && formData.image)) && (
                    <div className="relative">
                      <img
                        src={imagePreview || getImageUrl(formData.image)}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                      />
                      {imagePreview && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedImage(null);
                            setImagePreview(null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, false)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500">
                    Upload an image for the country (JPG, PNG, etc.)
                  </p>
                </div>
              </div>
            </>
          );

        case "city":
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <select
                  value={formData.countryId || ""}
                  onChange={(e) =>
                    handleFormChange("countryId", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country._id} value={country._id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone *
                </label>
                <input
                  type="text"
                  value={formData.timezone || ""}
                  onChange={(e) => handleFormChange("timezone", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City Image
                </label>
                <div className="space-y-4">
                  {(imagePreview || (isEdit && formData.image)) && (
                    <div className="relative">
                      <img
                        src={imagePreview || getImageUrl(formData.image)}
                        alt="Preview"
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                      />
                      {imagePreview && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedImage(null);
                            setImagePreview(null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, false)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500">
                    Upload an image for the city (JPG, PNG, etc.)
                  </p>
                </div>
              </div>
            </>
          );

        case "activity":
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <select
                  value={formData.cityId || ""}
                  onChange={(e) => handleFormChange("cityId", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city._id} value={city._id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes) *
                </label>
                <input
                  type="number"
                  value={formData.duration || ""}
                  onChange={(e) =>
                    handleFormChange("duration", parseInt(e.target.value))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Capacity *
                </label>
                <input
                  type="number"
                  value={formData.maxCapacity || ""}
                  onChange={(e) =>
                    handleFormChange("maxCapacity", parseInt(e.target.value))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity Images (up to 5 images)
                </label>
                <div className="space-y-4">
                  {/* Existing Images */}
                  {existingImages.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {existingImages.map((img, idx) => (
                        <div key={idx} className="relative">
                          <img
                            src={getImageUrl(img)}
                            alt={`Existing ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(idx)}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                          >
                            <FaTimes size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* New Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {imagePreviews.map((preview, idx) => (
                        <div key={idx} className="relative">
                          <img
                            src={preview}
                            alt={`Preview ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                          >
                            <FaTimes size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageSelect(e, true)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={imagePreviews.length + existingImages.length >= 5}
                  />
                  <p className="text-xs text-gray-500">
                    Upload up to 5 images for the activity (JPG, PNG, etc.)
                    {imagePreviews.length + existingImages.length > 0 && (
                      <span className="ml-2">
                        ({imagePreviews.length + existingImages.length}/5)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </>
          );

        case "option":
          // Get filtered cities based on selected country
          const filteredCitiesForOption = cities.filter((city) => {
            const cityCountryId = city.countryId && typeof city.countryId === 'object' 
              ? city.countryId._id 
              : city.countryId;
            return !optionSelectedCountry || cityCountryId === optionSelectedCountry || cityCountryId?.toString() === optionSelectedCountry;
          });

          // Get filtered activities based on selected city
          const filteredActivitiesForOption = activities.filter((activity) => {
            const activityCityId = activity.cityId && typeof activity.cityId === 'object' 
              ? activity.cityId._id 
              : activity.cityId;
            return !optionSelectedCity || activityCityId === optionSelectedCity || activityCityId?.toString() === optionSelectedCity;
          });

          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <select
                  value={optionSelectedCountry || ""}
                  onChange={(e) => {
                    setOptionSelectedCountry(e.target.value);
                    setOptionSelectedCity(""); // Reset city when country changes
                    handleFormChange("activityId", ""); // Reset activity when country changes
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country._id} value={country._id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <select
                  value={optionSelectedCity || ""}
                  onChange={(e) => {
                    setOptionSelectedCity(e.target.value);
                    handleFormChange("activityId", ""); // Reset activity when city changes
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={!optionSelectedCountry}
                >
                  <option value="">Select City</option>
                  {filteredCitiesForOption.map((city) => (
                    <option key={city._id} value={city._id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity *
                </label>
                <select
                  value={formData.activityId || ""}
                  onChange={(e) =>
                    handleFormChange("activityId", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={!optionSelectedCity}
                >
                  <option value="">Select Activity</option>
                  {filteredActivitiesForOption.map((activity) => (
                    <option key={activity._id} value={activity._id}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Option Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Price (₹) *
                </label>
                <input
                  type="number"
                  value={formData.basePrice || ""}
                  onChange={(e) =>
                    handleFormChange("basePrice", parseFloat(e.target.value))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes) *
                </label>
                <input
                  type="number"
                  value={formData.duration || ""}
                  onChange={(e) =>
                    handleFormChange("duration", parseInt(e.target.value))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Capacity *
                </label>
                <input
                  type="number"
                  value={formData.maxCapacity || ""}
                  onChange={(e) =>
                    handleFormChange("maxCapacity", parseInt(e.target.value))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>
            </>
          );

        // renderModal() function mein time-pricing case ko update karo:
        case "time-pricing":
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity *
                </label>
                <select
                  value={formData.activityId || ""}
                  onChange={(e) =>
                    handleFormChange("activityId", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Activity</option>
                  {activities.map((activity) => (
                    <option key={activity._id} value={activity._id}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day Type *
                </label>
                <select
                  value={formData.dayType || "weekday"}
                  onChange={(e) => handleFormChange("dayType", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="weekday">Weekday</option>
                  <option value="weekend">Weekend</option>
                  <option value="holiday">Holiday</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time (HH:MM) *
                </label>
                <input
                  type="time"
                  value={formData.startTime || "09:00"}
                  onChange={(e) =>
                    handleFormChange("startTime", e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time (HH:MM) *
                </label>
                <input
                  type="time"
                  value={formData.endTime || "17:00"}
                  onChange={(e) => handleFormChange("endTime", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Multiplier *
                </label>
                <input
                  type="number"
                  value={formData.multiplier || 1.0}
                  onChange={(e) =>
                    handleFormChange("multiplier", parseFloat(e.target.value))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0.1"
                  step="0.1"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  1.0 = Normal price, 1.5 = 50% extra, 0.8 = 20% discount
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Label/Name *
                </label>
                <input
                  type="text"
                  value={formData.label || ""}
                  onChange={(e) => handleFormChange("label", e.target.value)}
                  placeholder="e.g., Peak Hours, Morning Slot, Evening Discount"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </>
          );
        case "visitor-type":
          return (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visitor Type Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  placeholder="e.g., Adult, Child, Senior"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Age *
                </label>
                <input
                  type="number"
                  value={formData.minAge || ""}
                  onChange={(e) =>
                    handleFormChange("minAge", parseInt(e.target.value))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Age *
                </label>
                <input
                  type="number"
                  value={formData.maxAge || ""}
                  onChange={(e) =>
                    handleFormChange("maxAge", parseInt(e.target.value))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Multiplier *
                </label>
                <input
                  type="number"
                  value={formData.priceMultiplier || ""}
                  onChange={(e) =>
                    handleFormChange(
                      "priceMultiplier",
                      parseFloat(e.target.value)
                    )
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0.1"
                  step="0.1"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  1.0 = Normal price, 0.5 = Half price, 1.5 = 50% more
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  placeholder="Optional description"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>
            </>
          );
        default:
          return (
            <div className="text-center py-8">
              <p className="text-gray-500">
                Form for {type} will be implemented in the next update
              </p>
            </div>
          );
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-900">
              {getModalTitle()}
            </h3>
            <button
              onClick={() => {
                setShowAddModal(false);
                setEditingItem(null);
                setEditFormData({});
                resetFormAndImages(type);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <FaClose />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderFormFields()}
            </div>

            {/* Status field for editing */}
            {isEdit && (
              <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active || false}
                  onChange={(e) => handleFormChange("active", e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 text-sm text-gray-700">
                  Active
                </label>
              </div>
            )}

            <div className="mt-8 flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                  setEditFormData({});
                  resetFormAndImages(type);
                }}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <FaSave className="mr-2" />
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-md border-b border-gray-200">
        <div className="px-6 py-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                {isSidebarCollapsed ? <FaBars /> : <FaTimes />}
              </button>
              
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
          bg-white border-r border-gray-200 transition-all duration-300 overflow-y-auto
          ${isSidebarCollapsed ? "w-0 md:w-20" : "w-64"}
          ${isSidebarCollapsed ? "hidden md:block" : "block"}
        `}
        >
          <div className="p-4">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setSearchTerm("");
                    setCountryFilter("");
                    setCityFilter("");
                    setActivityFilter("");
                    setOptionCountryFilter("");
                    setOptionCityFilter("");
                    setOptionActivityFilter("");
                  }}
                  className={`
                    w-full flex items-center px-3 py-2.5 rounded-lg transition-colors
                    ${
                      activeSection === item.id
                        ? "bg-blue-50 text-blue-600"
                        : "text-gray-700 hover:bg-gray-100"
                    }
                  `}
                >
                  <div className={`${isSidebarCollapsed ? "mx-auto" : "mr-3"}`}>
                    {item.icon}
                  </div>
                  {!isSidebarCollapsed && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main
          className={`flex-1 p-6 transition-all duration-300 ${
            isSidebarCollapsed ? "md:ml-20" : "ml-0"
          }`}
        >
          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Content Area */}
              <div>
                {activeSection === "dashboard" && renderDashboard()}
                {activeSection === "countries" && renderCountries()}
                {activeSection === "cities" && renderCities()}
                {activeSection === "activities" && renderActivities()}
                {activeSection === "options" && renderOptions()}
                {activeSection === "visitor-types" && renderVisitorTypes()}{" "}
                {activeSection === "time-pricing" && renderTimePricing()}
                {activeSection === "bookings" && renderBookings()}
                {activeSection === "reports" && renderReports()}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Modals */}
      {renderModal()}
    </div>
  );
};

export default AdminActivity;
